import { useFeature } from '../auth/features';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { caseService, watchlistService, investigationService, searchService, authService, userService, legalService, caseWorkerService, caseRunsService, canvasJobService, type CaseRun } from '../services/api';
import { toast } from 'sonner';
import { useConfirmDialog } from '../components/DialogHost';
import {
    FileText, ArrowLeft, Edit2, Save, X,
    Trash2, Plus, MapPin, ChevronRight, ChevronDown, Loader2, AlertTriangle,
    CheckCircle, Clock, ExternalLink, Download, ShieldCheck, Brain,
    Activity, Image, List, BarChart2, Pencil, Zap, Users, UserPlus,
    Send, MailCheck, Scale, Banknote, Sparkles, Layers, Eye
} from 'lucide-react';



import { ForensicEvidenceSection } from '../components/cases/ForensicEvidenceSection';
import { ForensicImportModal } from '../components/cases/ForensicImportModal';
import { CaseSummary } from '../components/cases/CaseSummary';
import { CaseRunsSection } from '../components/cases/CaseRunsSection';
import { CaseReportsSection } from '../components/cases/CaseReportsSection';
import { ProposeSourceCard } from '../components/cases/ProposeSourceCard';
import { NextStepsPanel, latestRecordedRun } from '../components/cases/NextStepsPanel';
import { PivotPanel } from '../components/report/PivotPanel';
import { NoticeTemplatesPanel } from '../components/cases/NoticeTemplatesPanel';
import { useCanvasJobWatch } from '../components/cases/useCanvasJobWatch';
import {
    describeJobProgress, findActiveRun, formatDurationSeconds, runErrorMessage, secondsSince, startAutoInvestigateRun,
} from '../components/cases/caseRuns';
import { PageBreadcrumb } from '../components/PageBreadcrumb';
import { brandGhostBtn, brandIconAccent } from '../components/brandChrome';
import { IntelligenceNoteView } from '../components/ui/IntelligenceNoteView';
import { CaseWorkbenchEntryModal } from '../components/workbench/CaseWorkbenchEntryModal';
import {
    buildReportFilename,
    reportTypeLabel,
    subjectFromMetadata,
    subjectIdentifier,
} from '../utils/reportArtifacts';
import { VIEWER_ACTION_REASON } from '../case-canvas/caseAccess';
import { caseCanvasUrl } from '../case-canvas/landingTab';

interface Case {
    id: string;
    title: string;
    fir_number: string | null;
    officer_name: string | null;
    status: 'OPEN' | 'PROCESSING' | 'CLOSED' | 'ARCHIVED';
    notes: string | null;
    created_at: string;
    updated_at: string;
    created_by?: number | null;
    creator_username?: string | null;
    creator_full_name?: string | null;
    creator_email?: string | null;
    /** The caller may change this case (owner, owner's admin, super admin, editor collaborator). Absent reads as read-only. */
    can_edit?: boolean | null;
}

interface CaseCollaborator {
    user_id: number;
    username?: string | null;
    full_name?: string | null;
    role: string;
    added_by?: number | null;
    added_at?: string | null;
}

interface WatchlistItem {
    id: string;
    address: string;
    chain: string;
    label: string | null;
    notes: string | null;
    is_active: boolean;
    created_at: string;
}

interface CaseEvidence {
    id: number;
    case_id: string;
    evidence_type: string;
    title: string;
    description?: string | null;
    payload?: Record<string, any>;
    source?: string | null;
    source_ref?: string | null;
    collected_by?: string | null;
    collected_at?: string | null;
    hash_sha256?: string | null;
    created_at: string;
    updated_at: string;
}

interface Investigation {
    id: string;
    case_id: string;
    name: string;
    investigation_type?: 'fusion' | 'crypto' | 'bank' | 'telecom' | string;
    root_address?: string | null;
    root_chain?: string | null;
    notes?: string | null;
    graph_state?: Record<string, any>;
    created_at: string;
    updated_at: string;
}

interface InvestigationReportArtifact {
    id: string;
    title: string;
    report_type: string;
    report_format: string;
    created_at: string;
    content_sha256: string;
    size_bytes: number;
    metadata?: Record<string, any>;
}

// ── Investigation Drawer Card ────────────────────────────────────────────────
const InvestigationCard: React.FC<{
    inv: Investigation;
    caseId: string;
    onUpdated: (updated: Investigation) => void;
    onDeleted: (id: string) => void;
}> = ({ inv, caseId, onUpdated, onDeleted: _onDeleted }) => {
    const navigate = useNavigate();
    const { confirm } = useConfirmDialog();
    const [expanded, setExpanded] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [newName, setNewName] = useState(inv.name);
    const [downloading, setDownloading] = useState(false);
    const [savingName, setSavingName] = useState(false);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [storedReports, setStoredReports] = useState<InvestigationReportArtifact[]>([]);
    const [fullInv, setFullInv] = useState<Investigation>(inv);

    useEffect(() => {
        setFullInv(inv);
    }, [inv]);

    useEffect(() => {
        const shouldHydrate = expanded && (!fullInv.graph_state || Object.keys(fullInv.graph_state || {}).length === 0);
        if (!shouldHydrate) return;
        let cancelled = false;
        (async () => {
            try {
                const detailed = await investigationService.get(inv.id, caseId);
                if (cancelled) return;
                setFullInv(detailed);
                onUpdated(detailed);
            } catch {
                // Best effort: keep lightweight card rendering
            }
        })();
        return () => { cancelled = true; };
    }, [expanded, fullInv.graph_state, inv.id, caseId, onUpdated]);

    const isFusion = inv.investigation_type === 'fusion' || (inv.name && inv.name.toLowerCase().includes('fusion'));
    const gs = fullInv.graph_state || {};
    const elements = gs.elements || {};
    const nodeCount = Array.isArray(elements.nodes) ? elements.nodes.length
        : Array.isArray(elements) ? elements.filter((e: any) => !e?.data?.source).length : '?';
    const edgeCount = Array.isArray(elements.edges) ? elements.edges.length
        : Array.isArray(elements) ? elements.filter((e: any) => e?.data?.source).length : '?';
    const hasAI = !!gs.intelligence_summary;
    const hasPatterns = !!(gs.pattern_scans?.typologies?.length);
    const hasScreenshot = !!gs.screenshot;
    const hasActionLog = !!(gs.action_log?.length);

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadReport = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            setDownloading(true);
            // Prefer stored HTML report (from Auto-Investigate "View LE Report")
            // over the legacy PDF generator which has data format issues.
            const reports = storedReports.length > 0
                ? storedReports
                : await investigationService.listReportArtifacts(caseId, inv.id).catch(() => []);
            if (reports && reports.length > 0) {
                // Open latest stored HTML report in new tab
                const latest = reports[0];
                const report = await investigationService.getReportArtifact(caseId, inv.id, latest.id);
                const win = window.open('', '_blank');
                if (win) {
                    win.document.write(report.html_content || '');
                    win.document.close();
                    toast.success('Investigation report opened');
                } else {
                    // Fallback: download as HTML file
                    const blob = new Blob([report.html_content || ''], { type: 'text/html' });
                    downloadBlob(blob, `investigation-${inv.name.replace(/\s+/g, '_').slice(0, 40)}.html`);
                    toast.success('Investigation report downloaded');
                }
            } else {
                // No stored HTML report — the legacy Jinja PDF endpoint is
                // deprecated (drifts from the React renderer). Nudge the user
                // to the Workbench to generate the canonical HTML artifact.
                toast.error(
                    'No saved report for this investigation. Open it in the ' +
                    'Workbench and click "View LE Report" to generate one.',
                    { duration: 6000 },
                );
            }
        } catch {
            toast.error('Failed to generate report');
        } finally {
            setDownloading(false);
        }
    };

    const handleSaveName = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!newName.trim() || newName === inv.name) { setRenaming(false); return; }
        try {
            setSavingName(true);
            const updated = await investigationService.update(caseId, inv.id, { name: newName.trim() });
            onUpdated(updated);
            setRenaming(false);
            toast.success('Investigation renamed');
        } catch {
            toast.error('Failed to rename investigation');
        } finally {
            setSavingName(false);
        }
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const ok = await confirm({
            title: 'Delete this investigation?',
            message: 'The graph snapshot and all metadata will be permanently removed.',
            confirmText: 'Delete',
            danger: true,
        });
        if (!ok) return;
        // No delete API yet — inform and skip
        toast.error('Investigation delete endpoint not yet available. Coming soon.');
        // onDeleted(inv.id);
    };

    const loadStoredReports = useCallback(async () => {
        try {
            setReportsLoading(true);
            const list = await investigationService.listReportArtifacts(caseId, inv.id);
            setStoredReports(list || []);
        } catch {
            setStoredReports([]);
        } finally {
            setReportsLoading(false);
        }
    }, [caseId, inv.id]);

    useEffect(() => {
        if (expanded) {
            loadStoredReports();
        }
    }, [expanded, loadStoredReports]);

    const handleOpenStoredReport = async (reportId: string) => {
        try {
            const report = await investigationService.getReportArtifact(caseId, inv.id, reportId);
            const win = window.open('', '_blank');
            if (!win) {
                toast.error('Pop-up blocked. Allow pop-ups to view report.');
                return;
            }
            win.document.write(report.html_content || '');
            win.document.close();
        } catch {
            toast.error('Failed to open stored report');
        }
    };

    const handleDownloadStoredReport = async (reportId: string, title: string, report?: InvestigationReportArtifact) => {
        try {
            const detail = await investigationService.getReportArtifact(caseId, inv.id, reportId);
            const blob = new Blob([detail.html_content || ''], { type: 'text/html' });
            const subject = subjectFromMetadata(report?.metadata || detail.metadata);
            const type = report?.report_type || detail.report_type || 'report';
            const safe = buildReportFilename(type, subject)
                || (title || 'report').replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_').slice(0, 60);
            downloadBlob(blob, `${safe || 'investigation-report'}.html`);
            toast.success('Stored report downloaded');
        } catch {
            toast.error('Failed to download stored report');
        }
    };

    return (
        <div className="border border-border rounded-lg overflow-hidden bg-card transition-all">
            {/* Row Header — always visible */}
            <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-secondary/30 transition-colors select-none"
                onClick={() => setExpanded(!expanded)}
            >
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${expanded ? '' : '-rotate-90'}`} />

                {/* Name / Rename Input */}
                {renaming ? (
                    <div className="flex items-center gap-2 flex-1" onClick={e => e.stopPropagation()}>
                        <input
                            autoFocus
                            className="bg-background border border-border rounded px-2 py-1 text-sm flex-1"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveName(e as any); if (e.key === 'Escape') setRenaming(false); }}
                        />
                        <button onClick={handleSaveName} disabled={savingName} className="text-green-400 hover:text-green-300" title="Save name">
                            <Save className="w-4 h-4" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); setRenaming(false); }} className="text-muted-foreground hover:text-foreground" title="Cancel">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-primary truncate">{inv.name}</span>
                            {isFusion ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                                    <Sparkles className="w-3 h-3 text-amber-400" /> Fusion Investigation
                                </span>
                            ) : (
                                <span className="px-2 py-0.5 text-xs font-mono rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1">
                                    ⛓️ Crypto Trace
                                </span>
                            )}
                            <button onClick={e => { e.stopPropagation(); setRenaming(true); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity" title="Rename">
                                <Pencil className="w-3 h-3" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                            {inv.root_chain && <span className="px-1.5 py-0.5 text-xs bg-secondary rounded font-mono">{inv.root_chain}</span>}
                            {inv.root_address && <span className="text-xs text-muted-foreground font-mono truncate max-w-[180px]">{inv.root_address}</span>}
                            {isFusion && !inv.root_address && <span className="text-xs text-muted-foreground">Multi-modal canvas & intelligence</span>}
                        </div>
                    </div>
                )}

                {/* Metadata badges */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {hasAI && <span title="AI Summary" className="p-1 bg-primary/10 text-primary rounded"><Brain className="w-3 h-3" /></span>}
                    {hasPatterns && <span title="Pattern Scans" className="p-1 bg-amber-500/10 text-amber-400 rounded"><Zap className="w-3 h-3" /></span>}
                    {hasScreenshot && <span title="Graph Screenshot" className="p-1 bg-emerald-500/10 text-emerald-400 rounded"><Image className="w-3 h-3" /></span>}
                    <span className="text-xs text-muted-foreground ml-1">{new Date(inv.updated_at).toLocaleDateString()}</span>
                </div>
            </div>

            {/* Expanded Drawer */}
            {expanded && (
                <div className="border-t border-border">
                    {/* Action Bar */}
                    <div className="flex gap-2 p-3 bg-secondary/20 flex-wrap">
                        {isFusion ? (
                            <>
                                <button
                                    onClick={() => navigate(`/cases/${caseId}/canvas`)}
                                    className={FUSION_CANVAS_BTN}
                                >
                                    <Sparkles className="w-3.5 h-3.5" /> Open Fusion Canvas
                                </button>
                                <button
                                    onClick={() => navigate(`/workbench?investigation_id=${inv.id}&case_id=${caseId}`)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded ${brandGhostBtn}`}
                                >
                                    <ExternalLink className="w-3.5 h-3.5" /> Open Workbench
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => navigate(`/workbench?investigation_id=${inv.id}&case_id=${caseId}`)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded ${brandGhostBtn}`}
                                >
                                    <ExternalLink className="w-3.5 h-3.5" /> Resume in Workbench
                                </button>
                                <button
                                    onClick={() => navigate(`/cases/${caseId}/canvas`)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                                >
                                    <Sparkles className="w-3.5 h-3.5 text-amber-400" /> View in Fusion
                                </button>
                            </>
                        )}
                        <button
                            onClick={handleDownloadReport}
                            disabled={downloading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded transition-colors disabled:opacity-50"
                        >
                            <Download className="w-3.5 h-3.5" />
                            {downloading ? 'Loading...' : 'View Investigation Report'}
                        </button>
                        <button
                            onClick={e => { e.stopPropagation(); setRenaming(true); setExpanded(true); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-secondary hover:bg-secondary/80 rounded transition-colors"
                        >
                            <Pencil className="w-3.5 h-3.5" /> Rename
                        </button>
                        <button
                            onClick={handleDelete}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded transition-colors ml-auto"
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                    </div>

                    <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Left: Graph Thumbnail + Stats */}
                        <div className="space-y-3">
                            {hasScreenshot ? (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><Image className="w-3 h-3" /> Graph Capture</p>
                                    <img
                                        src={gs.screenshot}
                                        alt="Graph snapshot"
                                        className="w-full rounded border border-border object-contain bg-card"
                                        style={{ maxHeight: 200 }}
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-24 rounded border border-dashed border-border text-muted-foreground text-sm">
                                    No graph screenshot captured
                                </div>
                            )}

                            {/* Stats row */}
                            <div className="grid grid-cols-2 gap-2">
                                <div className="bg-secondary/30 rounded p-2 text-center">
                                    <div className="text-lg font-bold text-foreground">{nodeCount}</div>
                                    <div className="text-xs text-muted-foreground">Nodes</div>
                                </div>
                                <div className="bg-secondary/30 rounded p-2 text-center">
                                    <div className="text-lg font-bold text-foreground">{edgeCount}</div>
                                    <div className="text-xs text-muted-foreground">{isFusion ? 'Connections' : 'Transactions'}</div>
                                </div>
                            </div>

                            {/* Action Log */}
                            {hasActionLog && (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><List className="w-3 h-3" /> Investigator Actions</p>
                                    <div className="space-y-1 max-h-28 overflow-y-auto">
                                        {(gs.action_log as string[]).map((a, i) => (
                                            <div key={i} className="text-xs text-foreground/80 flex items-start gap-1.5">
                                                <span className="text-muted-foreground mt-0.5">›</span> {a}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: AI Summary + Patterns */}
                        <div className="space-y-3">
                            {/* AI Narrative */}
                            {hasAI ? (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><Brain className="w-3 h-3" /> AI Intelligence Note</p>
                                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-2.5">
                                        <IntelligenceNoteView
                                            markdown={gs.intelligence_summary as string}
                                            maxHeightClass="max-h-48"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-xs text-muted-foreground italic">No AI summary generated for this snapshot.</div>
                            )}

                            {/* Pattern Scans */}
                            {hasPatterns && (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><Zap className="w-3 h-3" /> Pattern Scan Results</p>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                        {(gs.pattern_scans.typologies as any[]).map((t, i) => (
                                            <div key={i} className="flex items-start justify-between gap-2 text-xs bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5">
                                                <span className="text-amber-300 font-medium">{(t.type || t.name || '—').replace(/_/g, ' ')}</span>
                                                <span className="text-muted-foreground flex-shrink-0">{Math.round((t.confidence || 0) * 100)}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Node Intelligence Table */}
                            {gs.node_intelligence && Object.keys(gs.node_intelligence).length > 0 && (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><BarChart2 className="w-3 h-3" /> Node Intelligence</p>
                                    <div className="space-y-1 max-h-36 overflow-y-auto">
                                        {Object.entries(gs.node_intelligence as Record<string, any>).slice(0, 8).map(([addr, intel]) => {
                                            const risk = intel?.risk_score ?? 0;
                                            const riskColor = risk >= 0.7 ? 'text-red-400' : risk >= 0.4 ? 'text-amber-400' : 'text-green-400';
                                            return (
                                                <div key={addr} className="flex items-center gap-2 text-xs">
                                                    <span className={`font-bold w-8 text-right flex-shrink-0 ${riskColor}`}>{Math.round(risk * 100)}%</span>
                                                    <span className="font-mono text-muted-foreground truncate">{addr.slice(0, 16)}…</span>
                                                    {intel?.entity_name && <span className="text-foreground/80 flex-shrink-0">{intel.entity_name}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            {inv.notes && (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1.5">Notes</p>
                                    <p className="text-xs text-foreground/80 whitespace-pre-wrap">{inv.notes}</p>
                                </div>
                            )}

                            {/* Stored immutable reports */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <p className="text-xs text-muted-foreground">Stored Reports (immutable)</p>
                                    <button
                                        onClick={loadStoredReports}
                                        className="text-[10px] px-1.5 py-0.5 rounded bg-secondary hover:bg-secondary/80"
                                    >
                                        Refresh
                                    </button>
                                </div>
                                <p className="text-[10px] text-muted-foreground mb-1.5 leading-relaxed">
                                    Each run archives separately — Targeted Node Intelligence, Follow the Money,
                                    Holistic Multi-Node Trace, and AI Graph Summary. Open any file and use Print / Save as PDF.
                                </p>
                                {reportsLoading ? (
                                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Loading reports...
                                    </div>
                                ) : storedReports.length === 0 ? (
                                    <p className="text-xs text-muted-foreground">No stored reports yet.</p>
                                ) : (
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {storedReports.map(r => {
                                            const shortId = subjectIdentifier(subjectFromMetadata(r.metadata));
                                            return (
                                            <div key={r.id} className="bg-secondary/30 border border-border rounded px-2 py-1.5">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <p className="text-xs text-slate-200 truncate" title={r.title}>{r.title}</p>
                                                        <p className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5">
                                                            <span className="font-medium text-foreground/70">
                                                                {reportTypeLabel(r.report_type)}
                                                            </span>
                                                            {shortId && (
                                                                <span className="font-mono">{shortId}</span>
                                                            )}
                                                            <span>{new Date(r.created_at).toLocaleString()}</span>
                                                            <span>{(r.size_bytes / 1024).toFixed(1)} KB</span>
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => handleOpenStoredReport(r.id)}
                                                            className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20"
                                                        >
                                                            Open
                                                        </button>
                                                        <button
                                                            onClick={() => handleDownloadStoredReport(r.id, r.title, r)}
                                                            className="text-[10px] px-1.5 py-0.5 rounded bg-secondary hover:bg-secondary/80"
                                                        >
                                                            HTML
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Inline Editable Field ────────────────────────────────────────────────────
const InlineField: React.FC<{
    label: string;
    value: string;
    onSave: (v: string) => void;
    placeholder?: string;
    type?: 'text' | 'select';
    options?: string[];
    multiline?: boolean;
}> = ({ label, value, onSave, placeholder, type = 'text', options = [], multiline = false }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);

    useEffect(() => { setDraft(value); }, [value]);

    const commit = () => {
        if (draft !== value) onSave(draft);
        setEditing(false);
    };

    if (editing) {
        return (
            <div>
                <p className="text-xs text-muted-foreground mb-1">{label}</p>
                {type === 'select' ? (
                    <select
                        autoFocus
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={commit}
                        className="bg-background border border-primary rounded px-2 py-1 text-sm w-full outline-none"
                    >
                        {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                ) : multiline ? (
                    <textarea
                        autoFocus
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={commit}
                        rows={3}
                        className="bg-background border border-primary rounded px-2 py-1 text-sm w-full outline-none resize-none"
                    />
                ) : (
                    <input
                        autoFocus
                        type="text"
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
                        placeholder={placeholder}
                        className="bg-background border border-primary rounded px-2 py-1 text-sm w-full outline-none"
                    />
                )}
            </div>
        );
    }

    return (
        <div
            className="group cursor-pointer"
            onClick={() => setEditing(true)}
            title="Click to edit"
        >
            <p className="text-xs text-muted-foreground mb-1">{label}</p>
            <p className={`text-sm font-medium ${!value ? 'text-muted-foreground italic' : ''} flex items-center gap-1.5 group-hover:text-primary transition-colors`}>
                {value || placeholder || '—'}
                <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </p>
        </div>
    );
};

// ── Notice Evidence Row ───────────────────────────────────────────────────────
// Specialised renderer for case_evidence rows where evidence_type === 'notice'.
// Shows lifecycle status, prior-disclosure metadata, and quick actions
// (download PDF, mark sent, mark responded).

const BankStatementEvidenceRow: React.FC<{
    ev: CaseEvidence;
    onDelete: () => void;
    onOpenCanvas: () => void;
}> = ({ ev, onDelete, onOpenCanvas }) => {
    const p = ev.payload || {};
    const holder = typeof p.holder_name === 'string' ? p.holder_name : '';
    const account = typeof p.account_number === 'string' ? p.account_number : '';
    const ifsc = typeof p.ifsc === 'string' ? p.ifsc : '';
    const txCount = typeof p.transaction_count === 'number' ? p.transaction_count : null;
    const cpCount = typeof p.counterparty_count === 'number' ? p.counterparty_count : null;
    const debit = typeof p.debit_total === 'number' ? p.debit_total : null;
    const credit = typeof p.credit_total === 'number' ? p.credit_total : null;
    const deepLink = typeof p.deep_link === 'string' ? p.deep_link : '';
    const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

    return (
        <div className="p-3 hover:bg-secondary/30" data-testid="bank-statement-evidence-row">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-1.5 py-0.5 text-[10px] bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded font-mono uppercase flex items-center gap-1">
                            <Banknote className="w-3 h-3" /> bank statement
                        </span>
                        <p className="text-sm font-medium truncate">{ev.title}</p>
                    </div>
                    {(holder || account) && (
                        <p className="text-xs text-muted-foreground">
                            {holder && <span className="font-medium text-foreground">{holder}</span>}
                            {holder && account && ' · '}
                            {account && <span className="font-mono">{account}</span>}
                            {ifsc && <span className="font-mono"> · {ifsc}</span>}
                        </p>
                    )}
                    {ev.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ev.description}</p>}
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {txCount != null && <span>{txCount} txs</span>}
                        {cpCount != null && <span>{cpCount} counterparties</span>}
                        {debit != null && <span className="text-rose-500">Dr {inr(debit)}</span>}
                        {credit != null && <span className="text-emerald-600">Cr {inr(credit)}</span>}
                        <span>{new Date(ev.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2">
                        <button
                            type="button"
                            onClick={onOpenCanvas}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 rounded"
                        >
                            <Activity className="w-3 h-3" /> Fusion Canvas
                        </button>
                        {deepLink && (
                            <a
                                href={deepLink}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-secondary hover:bg-secondary/80 rounded"
                            >
                                <ExternalLink className="w-3 h-3" /> StatementFlow
                            </a>
                        )}
                    </div>
                </div>
                <button onClick={onDelete} className="p-1.5 hover:bg-red-500/10 text-red-400 rounded flex-shrink-0">
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

const NoticeEvidenceRow: React.FC<{
    ev: CaseEvidence;
    onDownload: () => void;
    onMarkSent: () => void;
    onMarkResponded: () => void;
    onDelete: () => void;
}> = ({ ev, onDownload, onMarkSent, onMarkResponded, onDelete }) => {
    const p = ev.payload || {};
    const status: string = p.status || 'drafted';
    const exchange: string = p.exchange || '—';
    const target: string = p.target_address || '';
    const chain: string = p.chain || '';
    const txHashes: string[] = Array.isArray(p.target_tx_hashes) ? p.target_tx_hashes : [];
    const templateKey: string = p.template_key || 'notice';
    const sentAt: string | null = p.sent_at || null;
    const respondedAt: string | null = p.response_received_at || null;
    const responseSummary: string | null = p.response_summary || null;
    const dueAt: string | null = p.response_due_at || null;
    const overdue = dueAt && !respondedAt && new Date(dueAt) < new Date();

    const statusBadge = {
        drafted:   'bg-slate-500/10 text-slate-300 border border-slate-500/20',
        sent:      'bg-amber-500/10 text-amber-300 border border-amber-500/20',
        responded: 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20',
        escalated: 'bg-red-500/10 text-red-300 border border-red-500/20',
    }[status] || 'bg-slate-500/10 text-slate-300';

    return (
        <div className="p-3 hover:bg-secondary/30">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="px-1.5 py-0.5 text-[10px] bg-primary/15 text-primary rounded font-mono uppercase flex items-center gap-1">
                            <Scale className="w-3 h-3" /> {templateKey}
                        </span>
                        <span className={`px-1.5 py-0.5 text-[10px] rounded uppercase font-medium ${statusBadge}`}>{status}</span>
                        {overdue && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-red-500/15 text-red-300 rounded uppercase font-medium border border-red-500/20">overdue</span>
                        )}
                        <p className="text-sm font-medium truncate">{exchange}</p>
                    </div>

                    {target && (
                        <p className="text-xs text-muted-foreground font-mono truncate">
                            {chain && <span className="text-primary">{chain} </span>}
                            {target.slice(0, 20)}…{target.slice(-8)}
                            {txHashes.length > 0 && <span className="text-muted-foreground"> · {txHashes.length} tx{txHashes.length > 1 ? 's' : ''}</span>}
                        </p>
                    )}

                    {ev.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ev.description}</p>}

                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span>Drafted {new Date(ev.created_at).toLocaleDateString()}</span>
                        {sentAt && <span>Sent {new Date(sentAt).toLocaleDateString()}</span>}
                        {dueAt && !respondedAt && <span>Due {new Date(dueAt).toLocaleDateString()}</span>}
                        {respondedAt && <span className="text-emerald-400">Responded {new Date(respondedAt).toLocaleDateString()}</span>}
                    </div>

                    {responseSummary && (
                        <div className="mt-1.5 p-2 bg-emerald-500/5 border-l-2 border-emerald-500/40 rounded-r text-xs text-foreground/80">
                            <span className="text-emerald-400 font-medium">Response: </span>{responseSummary}
                        </div>
                    )}

                    <div className="flex items-center gap-1.5 mt-2">
                        <button
                            onClick={onDownload}
                            disabled={!p.pdf_base64}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                            title={p.pdf_base64 ? 'Download the stored PDF' : 'No PDF stored for this notice'}
                        >
                            <Download className="w-3 h-3" /> PDF
                        </button>
                        {status === 'drafted' && (
                            <button
                                onClick={onMarkSent}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 rounded"
                                title="Mark as sent to the exchange"
                            >
                                <Send className="w-3 h-3" /> Mark Sent
                            </button>
                        )}
                        {status !== 'responded' && (
                            <button
                                onClick={onMarkResponded}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 rounded"
                                title="Record the exchange's response"
                            >
                                <MailCheck className="w-3 h-3" /> Mark Responded
                            </button>
                        )}
                    </div>
                </div>

                <button onClick={onDelete} className="p-1.5 hover:bg-red-500/10 text-red-400 rounded flex-shrink-0" title="Delete notice (does not retract it)">
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};


/** Server budget (600 s) plus slack: a sessionStorage "running" flag older than this is stale. */
// One style for the one action "Open Fusion Canvas" (case header and investigation drawer).
const FUSION_CANVAS_BTN =
    'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors shadow-sm';

const AUTO_INVESTIGATE_STALE_MS = 12 * 60 * 1000;

// ── Main CaseDetail Component ─────────────────────────────────────────────────
export const CaseDetail: React.FC = () => {
    // Served feature matrix (auth/features.ts), not localStorage/getMe role strings.
    const isSuperAdmin = useFeature('admin.super');
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [caseData, setCaseData] = useState<Case | null>(null);
    const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([]);
    const [investigations, setInvestigations] = useState<Investigation[]>([]);
    const [evidence, setEvidence] = useState<CaseEvidence[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState<'pdf' | 'json' | 'csv' | null>(null);
    // Device-extraction / offline-manifest upload, scoped to this case so the
    // .manifest.ndjson re-import path (which needs a case context) is reachable.
    const [showDeviceUpload, setShowDeviceUpload] = useState(false);
    const [isSavingCase, setIsSavingCase] = useState(false);
    const [showEvidenceModal, setShowEvidenceModal] = useState(false);
    const [workbenchEntryOpen, setWorkbenchEntryOpen] = useState(false);
    const [workbenchEntryPreferFresh, setWorkbenchEntryPreferFresh] = useState(false);
    const [evidenceForm, setEvidenceForm] = useState({
        evidence_type: 'note', title: '', description: '', source: '', hash_sha256: ''
    });
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [collaborators, setCollaborators] = useState<CaseCollaborator[]>([]);
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [showAddCollab, setShowAddCollab] = useState(false);
    const [addingCollab, setAddingCollab] = useState(false);
    // Auto-Investigate: one long request (server budget 600 s). The start time
    // is kept in sessionStorage per case so a reload neither double-runs nor
    // loses the "running" state; it expires after the server budget plus slack.
    const autoInvestigateKey = id ? `caseWorker.autoInvestigate.running.${id}` : null;
    const readAutoInvestigateStart = useCallback((): number | null => {
        if (!autoInvestigateKey) return null;
        try {
            const raw = sessionStorage.getItem(autoInvestigateKey);
            const started = raw ? Number(raw) : NaN;
            if (!Number.isFinite(started)) return null;
            if (Date.now() - started > AUTO_INVESTIGATE_STALE_MS) {
                sessionStorage.removeItem(autoInvestigateKey);
                return null;
            }
            return started;
        } catch {
            return null;
        }
    }, [autoInvestigateKey]);
    const [autoInvestigateStartedAt, setAutoInvestigateStartedAt] = useState<number | null>(() => readAutoInvestigateStart());
    const autoInvestigating = autoInvestigateStartedAt !== null;
    const [autoInvestigateElapsed, setAutoInvestigateElapsed] = useState(0);
    const { confirm, confirmDialog } = useConfirmDialog();

    useEffect(() => {
        setAutoInvestigateStartedAt(readAutoInvestigateStart());
    }, [readAutoInvestigateStart]);

    useEffect(() => {
        if (autoInvestigateStartedAt === null) { setAutoInvestigateElapsed(0); return; }
        const tick = () => {
            const elapsed = Date.now() - autoInvestigateStartedAt;
            setAutoInvestigateElapsed(Math.floor(elapsed / 1000));
            // A run restored after a reload has no promise to settle it; release
            // the button once the server budget has certainly passed.
            if (elapsed > AUTO_INVESTIGATE_STALE_MS) {
                try { if (autoInvestigateKey) sessionStorage.removeItem(autoInvestigateKey); } catch { /* ignore */ }
                setAutoInvestigateStartedAt(null);
            }
        };
        tick();
        const timer = window.setInterval(tick, 1000);
        return () => window.clearInterval(timer);
    }, [autoInvestigateStartedAt, autoInvestigateKey]);

    // ── Runs (GET /cases/{id}/runs) and the background Auto-Investigate job ──
    // The run starts as a `case_worker` canvas job (?async=1) and is watched by
    // polling GET /canvas/{id}/jobs/{job_id} every 5 s. A run that is still going
    // when the page loads (reload, another tab) is picked up from the runs list.
    const [runs, setRuns] = useState<CaseRun[]>([]);
    const [spineCounts, setSpineCounts] = useState<{ journal: number | null; nodes: number | null; edges: number | null }>(
        { journal: null, nodes: null, edges: null },
    );
    const [runsLoading, setRunsLoading] = useState(false);
    const [runsError, setRunsError] = useState<string | null>(null);
    const [runsNonce, setRunsNonce] = useState(0);
    const [activeJobId, setActiveJobId] = useState<string | null>(null);
    const [activeJobLocalStart, setActiveJobLocalStart] = useState<number | null>(null);
    const [startingRun, setStartingRun] = useState(false);
    const [clock, setClock] = useState(() => Date.now());
    const canWatch = useFeature('case.watch');
    const [watchState, setWatchState] = useState<{
        enabled: boolean;
        wallets_watched: number;
        last_tick_at: string | null;
    } | null>(null);
    const [isTogglingWatch, setIsTogglingWatch] = useState(false);

    const loadWatch = useCallback(async () => {
        if (!id || !canWatch) return;
        try {
            const w = await caseService.getWatch(id);
            setWatchState(w);
        } catch {
            setWatchState(null);
        }
    }, [id, canWatch]);

    useEffect(() => {
        if (canWatch) {
            loadWatch();
        } else {
            setWatchState(null);
        }
    }, [canWatch, loadWatch]);

    const canEdit = caseData?.can_edit === true;
    const handleToggleWatch = async () => {
        if (!id || !canEdit || !canWatch || isTogglingWatch) return;
        setIsTogglingWatch(true);
        try {
            const next = !watchState?.enabled;
            const res = await caseService.setWatch(id, next);
            setWatchState(res);
            toast.success(`Case watch ${next ? 'enabled' : 'disabled'}`);
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to update case watch');
        } finally {
            setIsTogglingWatch(false);
        }
    };

    const finishedJobsRef = useRef<Set<string>>(new Set());
    const caseIdRef = useRef(id);
    useEffect(() => { caseIdRef.current = id; }, [id]);
    const refreshRuns = useCallback(() => setRunsNonce((n) => n + 1), []);
    const cancelRun = useCallback(async (run: CaseRun) => {
        if (!id || !run.job_id) return;
        try {
            await canvasJobService.cancel(id, run.job_id);
            toast.info('Cancel requested. The run stops on its next heartbeat.');
            refreshRuns();
        } catch (err: unknown) {
            toast.error(`Could not cancel the run: ${runErrorMessage(err)}`);
        }
    }, [id, refreshRuns]);

    // A different case: forget the previous case's runs and job.
    useEffect(() => {
        setRuns([]);
        setSpineCounts({ journal: null, nodes: null, edges: null });
        setRunsError(null);
        setActiveJobId(null);
        setActiveJobLocalStart(null);
    }, [id]);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;   // R8: a slow answer for another case never lands
        setRunsLoading(true);
        caseRunsService.list(id)
            .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res?.runs) ? res.runs : [];
                setRuns(list);
                const graph = res?.graph_counts;
                const asCount = (v: unknown): number | null =>
                    (typeof v === 'number' && Number.isFinite(v) ? v : null);
                setSpineCounts({
                    journal: asCount(res?.journal_count),
                    nodes: asCount(graph?.nodes),
                    edges: asCount(graph?.edges),
                });
                setRunsError(null);
                const active = findActiveRun(list);
                if (active?.job_id && !finishedJobsRef.current.has(active.job_id)) {
                    const jobId = active.job_id;
                    setActiveJobId((cur) => cur ?? jobId);
                }
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const status = (err as { response?: { status?: number } })?.response?.status;
                setSpineCounts({ journal: null, nodes: null, edges: null });
                setRunsError(status === 403 ? 'You do not have access to the runs of this case.' : `Failed to load runs: ${runErrorMessage(err)}`);
            })
            .finally(() => { if (!cancelled) setRunsLoading(false); });
        return () => { cancelled = true; };
    }, [id, runsNonce]);

    const refreshInvestigationsFor = useCallback(async (caseId: string) => {
        try {
            const refreshed = await investigationService.listByCase(caseId, false);
            if (caseIdRef.current === caseId) setInvestigations(refreshed || []);
        } catch { /* the list keeps its last state */ }
    }, []);

    const jobWatch = useCanvasJobWatch(id, activeJobId, {
        onDone: (job) => {
            finishedJobsRef.current.add(job.job_id);
            setActiveJobId(null);
            setActiveJobLocalStart(null);
            if (job.status === 'succeeded') {
                toast.success('Auto-Investigate finished. Runs and investigations refreshed; open the Fusion Canvas to review.');
            } else if (job.status === 'cancelled') {
                toast.warning('Auto-Investigate was cancelled before it finished.');
            } else {
                toast.error(`Auto-Investigate failed: ${job.error || 'no error text was recorded'}`);
            }
            refreshRuns();
            const caseId = String(job.case_id || id || '');
            if (caseId) void refreshInvestigationsFor(caseId);
        },
        onLost: (message) => {
            if (activeJobId) finishedJobsRef.current.add(activeJobId);
            // Only a run started from this page is announced; a resumed one (for
            // example a collaborator's run this user cannot poll) drops quietly.
            if (activeJobLocalStart !== null) {
                toast.error(`Stopped following the Auto-Investigate run (${message}). Refresh the runs list for its state.`);
            }
            setActiveJobId(null);
            setActiveJobLocalStart(null);
            refreshRuns();
        },
    });
    const jobRunning = activeJobId !== null;
    const runBusy = autoInvestigating || jobRunning || startingRun;

    useEffect(() => {
        if (!jobRunning) return;
        setClock(Date.now());
        const timer = window.setInterval(() => setClock(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [jobRunning]);

    const jobStartIso = jobWatch.job?.started_at || jobWatch.job?.created_at || null;
    const jobElapsed = jobStartIso
        ? secondsSince(jobStartIso, clock)
        : (activeJobLocalStart ? Math.max(0, Math.floor((clock - activeJobLocalStart) / 1000)) : 0);
    const jobProgressText = describeJobProgress(jobWatch.job?.progress);

    /** Summary line of an investigation file returned by a synchronous run. */
    const announceRunResult = (result: unknown) => {
        const raw = result && typeof result === 'object' ? (result as { summary?: unknown }).summary : null;
        const s = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
        const parts: string[] = [];
        if (typeof s.crypto_targets_count === 'number') parts.push(`${s.crypto_targets_count} crypto targets`);
        if (typeof s.flow_edges === 'number') parts.push(`${s.flow_edges} flow edges`);
        if (typeof s.seizure_targets_count === 'number') parts.push(`${s.seizure_targets_count} seizure targets`);
        toast.success(`Auto-investigation finished${parts.length ? `: ${parts.join(', ')}` : ''}. Open the Fusion Canvas to review.`);
    };

    // A viewer collaborator reads the case and never starts a run on it; unknown reads as read-only.
    const canRunCase = caseData?.can_edit === true;
    const handleRunAutoInvestigate = async () => {
        if (!id || !autoInvestigateKey || !canRunCase) return;
        if (activeJobId || startingRun || readAutoInvestigateStart() !== null) {
            toast.info('An Auto-Investigate run for this case is already running.');
            return;
        }
        const caseId = id;
        setStartingRun(true);
        try {
            const outcome = await startAutoInvestigateRun(
                () => caseWorkerService.startAutoInvestigateJob(caseId, {
                    case_title: caseData?.title || undefined,
                    notes: caseData?.notes || undefined,
                }),
                () => {
                    setStartingRun(false);
                    return runAutoInvestigateSync();
                },
            );
            if (outcome.mode === 'job') {
                setActiveJobLocalStart(Date.now());
                setActiveJobId(outcome.job.job_id);
                toast.info(outcome.job.existing
                    ? 'An Auto-Investigate run is already going for this case; showing its progress.'
                    : 'Auto-Investigate started. Progress shows on this page; you can keep working.');
                refreshRuns();
            } else if (outcome.via === 'inline') {
                announceRunResult(outcome.result);
                refreshRuns();
                void refreshInvestigationsFor(caseId);
            }
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const msg = runErrorMessage(err);
            if (status === 409) toast.info(`Auto-Investigate is already running for this case: ${msg}`);
            else toast.error(`Auto-Investigate could not start: ${msg}`);
        } finally {
            setStartingRun(false);
        }
    };

    // Synchronous fallback (the request used before runs became jobs): one long
    // POST, used only when the async start fails with no answer or a server error.
    const runAutoInvestigateSync = async (): Promise<unknown> => {
        if (!id || !autoInvestigateKey) return null;
        const startedAt = Date.now();
        try { sessionStorage.setItem(autoInvestigateKey, String(startedAt)); } catch { /* private mode */ }
        setAutoInvestigateStartedAt(startedAt);
        try {
            toast.info('Auto-Investigate started (direct request). This can take several minutes; you can keep working.');
            const result = await caseWorkerService.autoInvestigateCase(id, {
                case_title: caseData?.title || undefined,
                notes: caseData?.notes || undefined,
            });
            announceRunResult(result);
            refreshRuns();
            await refreshInvestigationsFor(id);
            return result;
        } catch (err: any) {
            const timedOut = err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
            const msg = timedOut
                ? 'the request timed out in the browser; the server may still be writing results. Refresh the case in a few minutes before running again.'
                : runErrorMessage(err);
            toast.error(`Auto-investigate error: ${msg}`);
            return null;
        } finally {
            try { sessionStorage.removeItem(autoInvestigateKey); } catch { /* ignore */ }
            setAutoInvestigateStartedAt(null);
        }
    };

    const loadCollaborators = useCallback(async () => {
        if (!id) return;
        try {
            const collabs = await caseService.listCollaborators(id);
            setCollaborators(collabs || []);
        } catch {
            setCollaborators([]);
        }
    }, [id]);

    const loadCaseData = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const [caseResult, watchlistResult, investigationsResult, evidenceResult, me, collabsResult] = await Promise.all([
                caseService.get(id),
                watchlistService.getAll(id),
                investigationService.listByCase(id, false),
                caseService.listEvidence(id),
                authService.getMe().catch(() => null),
                caseService.listCollaborators(id).catch(() => [])
            ]);
            setCaseData(caseResult);
            setWatchlistItems(watchlistResult);
            setInvestigations(investigationsResult || []);
            setEvidence(evidenceResult || []);
            setCurrentUser(me);
            setCollaborators(collabsResult || []);
            searchService.recordView('case', caseResult.id, { title: caseResult.title, status: caseResult.status, entity_name: caseResult.title }).catch(console.error);
        } catch (err) {
            console.error('Failed to load case', err);
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 403) {
                setError('You do not have access to this case.');
            } else if (status === 401) {
                setError('Sign in required to open this case.');
            } else {
                setError('Failed to load case details. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { loadCaseData(); }, [loadCaseData]);

    // Inline case field save
    const handleFieldSave = async (field: keyof Case, value: string) => {
        if (!id || !caseData) return;
        try {
            setIsSavingCase(true);
            const updated = await caseService.update(id, { [field]: value });
            setCaseData(updated);
            toast.success('Case updated');
        } catch {
            toast.error('Failed to update case');
        } finally {
            setIsSavingCase(false);
        }
    };

    // Optimistic check — backend is authoritative. Allow: super_admin, case creator.
    // Parent-admin is harder to derive client-side; backend still accepts and
    // rejects via 403, so we hide the button for that case (can be enabled later).
    const canManageCollabs =
        !!currentUser && (
            isSuperAdmin ||
            (caseData?.created_by != null && currentUser.id === caseData.created_by)
        );

    const openAddCollab = async () => {
        if (!canManageCollabs) return;
        if (allUsers.length === 0) {
            try {
                const users = await userService.getAll();
                setAllUsers(users || []);
            } catch {
                toast.error('Failed to load users (are you admin?)');
                return;
            }
        }
        setShowAddCollab(true);
    };

    const handleAddCollab = async (userId: number) => {
        if (!id) return;
        setAddingCollab(true);
        try {
            await caseService.addCollaborator(id, userId, 'viewer');
            toast.success('Collaborator added');
            setShowAddCollab(false);
            await loadCollaborators();
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'Failed to add collaborator';
            toast.error(msg);
        } finally {
            setAddingCollab(false);
        }
    };

    const handleRemoveCollab = async (userId: number, name: string) => {
        if (!id) return;
        const ok = await confirm({
            title: 'Remove collaborator?',
            message: `Remove ${name} from this case? They will lose access.`,
            confirmText: 'Remove',
        });
        if (!ok) return;
        try {
            await caseService.removeCollaborator(id, userId);
            toast.success('Collaborator removed');
            await loadCollaborators();
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'Failed to remove collaborator';
            toast.error(msg);
        }
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = filename;
        document.body.appendChild(link); link.click(); link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleExportCase = async (format: 'pdf' | 'json' | 'csv') => {
        if (!id) return;
        try {
            setExporting(format);
            const result = await caseService.exportCase(id, format);
            downloadBlob(result.blob, `case-${id}.${format}`);
            toast.success(`Case exported (${format.toUpperCase()})`);
        } catch {
            toast.error('Failed to export case');
        } finally {
            setExporting(null);
        }
    };

    const handleDelete = async () => {
        const ok = await confirm({ title: 'Delete this case?', message: 'This cannot be undone.', confirmText: 'Delete', danger: true });
        if (!ok) return;
        try { await caseService.delete(id!); navigate('/cases'); toast.success('Case deleted'); }
        catch { toast.error('Failed to delete case'); }
    };

    const handleCreateEvidence = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        try {
            await caseService.addEvidence(id, { ...evidenceForm, payload: {} });
            toast.success('Evidence added');
            setShowEvidenceModal(false);
            setEvidenceForm({ evidence_type: 'note', title: '', description: '', source: '', hash_sha256: '' });
            loadCaseData();
        } catch { toast.error('Failed to add evidence'); }
    };

    const handleDeleteEvidence = async (evId: number) => {
        if (!id) return;
        const ok = await confirm({ title: 'Delete this evidence?', message: 'This will remove it from the evidence locker.', confirmText: 'Delete', danger: true });
        if (!ok) return;
        try { await caseService.deleteEvidence(id, evId); toast.success('Evidence deleted'); loadCaseData(); }
        catch { toast.error('Failed to delete evidence'); }
    };

    // ── Notice (subpoena) helpers — operate on case_evidence rows where
    // evidence_type === 'notice'. Lifecycle PATCH calls go through
    // legalService.updateNotice; status auto-advances on the backend.
    const handleDownloadNotice = (ev: CaseEvidence) => {
        const b64 = ev.payload?.pdf_base64;
        if (!b64) {
            toast.error('No PDF stored for this notice.');
            return;
        }
        try {
            const bin = atob(b64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const tk = ev.payload?.template_key || 'notice';
            const ex = ev.payload?.exchange || 'Exchange';
            const fir = ev.payload?.case_context?.fir_no || `id${ev.id}`;
            downloadBlob(blob, `Notice_${tk}_${ex}_${fir}.pdf`.replace(/\s+/g, '_'));
        } catch (err) {
            console.error('Failed to decode notice PDF:', err);
            toast.error('Failed to decode stored PDF (corrupted?).');
        }
    };

    const handleMarkNoticeSent = async (ev: CaseEvidence) => {
        if (!id) return;
        try {
            await legalService.updateNotice(ev.id, id, { sent_at: new Date().toISOString() });
            toast.success('Notice marked as sent.');
            loadCaseData();
        } catch {
            toast.error('Failed to update notice status.');
        }
    };

    const handleMarkNoticeResponded = async (ev: CaseEvidence) => {
        if (!id) return;
        const summary = window.prompt(
            `Response summary from ${ev.payload?.exchange || 'exchange'} (optional, free-text):`,
            ev.payload?.response_summary || '',
        );
        // null = user cancelled. Empty string = user kept blank, still proceed.
        if (summary === null) return;
        try {
            await legalService.updateNotice(ev.id, id, {
                response_received_at: new Date().toISOString(),
                response_summary: summary || undefined,
            });
            toast.success('Notice marked as responded.');
            loadCaseData();
        } catch {
            toast.error('Failed to update notice status.');
        }
    };

    const statusColors: Record<string, string> = {
        OPEN: 'bg-green-500/10 text-green-400 border-green-500/20',
        PROCESSING: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
        CLOSED: 'bg-slate-500/10 text-muted-foreground border-slate-500/20',
        ARCHIVED: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    };

    if (loading) return <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    if (error || !caseData) return (
        <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
            <AlertTriangle className="w-12 h-12 mb-4 text-red-500" />
            <p className="text-lg">{error || 'Case not found'}</p>
            <button onClick={() => navigate('/cases')} className="mt-4 flex items-center gap-2 text-primary hover:underline">
                <ArrowLeft className="w-4 h-4" /> Back to Cases
            </button>
        </div>
    );

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {/* ── Header ────────────────────────────────────────────── */}
            <div className="p-4 border-b border-border bg-card/50 flex-shrink-0">
                <PageBreadcrumb
                    className="mb-3"
                    items={[
                        { label: 'Dashboard', href: '/' },
                        { label: 'Cases', href: '/cases' },
                        { label: caseData.title },
                    ]}
                />
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/cases')} className="p-2 hover:bg-secondary rounded-lg transition-colors" aria-label="Back to cases">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-lg font-semibold truncate">{caseData.title}</h1>
                            <span className={`flex-shrink-0 flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${statusColors[caseData.status] || statusColors.ARCHIVED}`}>
                                {caseData.status === 'OPEN' ? <Clock className="w-3 h-3" />
                                    : caseData.status === 'PROCESSING' ? <Loader2 className="w-3 h-3" />
                                    : caseData.status === 'CLOSED' ? <CheckCircle className="w-3 h-3" />
                                    : <AlertTriangle className="w-3 h-3" />}
                                {caseData.status}
                            </span>
                            {(caseData.creator_full_name || caseData.creator_username) && (
                                <span className="flex-shrink-0 px-2 py-0.5 text-[10px] rounded-full bg-primary/10 text-primary border border-primary/20">
                                    Created by: {caseData.creator_full_name || caseData.creator_username}
                                </span>
                            )}
                            {isSavingCase && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">
                            <span title="Case ID — share this in URLs and audit logs" className="text-muted-foreground/80">ID: {id}</span>
                            <span className="mx-1.5 opacity-30">·</span>
                            {caseData.fir_number || 'No FIR Number'} · {investigations.length} investigation{investigations.length !== 1 ? 's' : ''} · {evidence.length} evidence
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            data-tour="case-open-fusion-canvas"
                            onClick={() => navigate(caseCanvasUrl(id!))}
                            className={FUSION_CANVAS_BTN}
                        >
                            <Sparkles className="w-3.5 h-3.5" /> Open Fusion Canvas
                        </button>
                        {/* Case Watch Toggle (WATCH01, R30, gated by case.watch F5) */}
                        {canWatch && (
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    data-tour="case-watch-toggle"
                                    data-testid="case-watch-toggle"
                                    onClick={handleToggleWatch}
                                    disabled={!canEdit || isTogglingWatch}
                                    title={
                                        !canEdit
                                            ? VIEWER_ACTION_REASON
                                            : watchState?.enabled
                                                ? `Watched (${watchState.wallets_watched} wallet${watchState.wallets_watched === 1 ? '' : 's'}${watchState.last_tick_at ? ` · Last checked: ${new Date(watchState.last_tick_at).toLocaleTimeString()}` : ''})`
                                                : 'Click to start automated re-screening every 6 hours'
                                    }
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md font-medium transition-colors border ${
                                        watchState?.enabled
                                            ? 'bg-amber-500/15 text-amber-500 border-amber-500/30 hover:bg-amber-500/25'
                                            : 'bg-secondary/60 text-muted-foreground border-border hover:bg-secondary hover:text-foreground'
                                    } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <Eye className="w-3.5 h-3.5" />
                                    <span>{watchState?.enabled ? 'Watching' : 'Watch this case'}</span>
                                    {watchState?.enabled && (
                                        <span className="text-[11px] tabular-nums font-mono opacity-80">
                                            ({watchState.wallets_watched}w)
                                        </span>
                                    )}
                                </button>
                                {watchState?.enabled && (
                                    <span data-testid="case-watch-meta" className="text-xs text-muted-foreground whitespace-nowrap">
                                        {watchState.wallets_watched} wallet{watchState.wallets_watched === 1 ? '' : 's'}
                                        {watchState.last_tick_at ? ` · Last checked: ${new Date(watchState.last_tick_at).toLocaleTimeString()}` : ' · Pending first tick'}
                                    </span>
                                )}
                            </div>
                        )}
                        <button
                            onClick={() => {
                                setWorkbenchEntryPreferFresh(false);
                                setWorkbenchEntryOpen(true);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-brand-gold-hover rounded-md font-medium transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" /> Workbench
                        </button>
                        <button onClick={handleDelete} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-md">
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                </div>
                {/* Case summary — at-a-glance posture (shared CaseSummary, also used by the Fusion demo) */}
                <div className="mt-3 pt-3 border-t border-border">
                    <CaseSummary
                        stats={[
                            { label: 'Investigations', value: investigations.length },
                            { label: 'Runs', value: runs.length },
                            {
                                label: 'Journal',
                                value: (
                                    <button
                                        type="button"
                                        data-testid="case-journal-count"
                                        className="font-semibold tabular-nums text-foreground bg-transparent border-0 p-0 cursor-pointer hover:underline"
                                        title="Open the case journal"
                                        onClick={() => navigate(`/cases/${id}/canvas?tab=journal`)}
                                    >
                                        {spineCounts.journal == null ? '—' : spineCounts.journal.toLocaleString('en-IN')}
                                    </button>
                                ),
                            },
                            {
                                label: 'Graph',
                                value: (
                                    <button
                                        type="button"
                                        data-testid="case-graph-count"
                                        className="font-semibold tabular-nums text-foreground bg-transparent border-0 p-0 cursor-pointer hover:underline"
                                        title="Entities on the case graph"
                                        onClick={() => navigate(`/cases/${id}/canvas`)}
                                    >
                                        {spineCounts.nodes == null ? '—' : spineCounts.nodes.toLocaleString('en-IN')}
                                    </button>
                                ),
                                sub: spineCounts.edges == null ? undefined : `${spineCounts.edges.toLocaleString('en-IN')} edges`,
                            },
                            { label: 'Evidence', value: evidence.length },
                            { label: 'Watchlist', value: watchlistItems.length, sub: 'addresses' },
                            { label: 'Collaborators', value: collaborators.length },
                            { label: 'Updated', value: new Date(caseData.updated_at).toLocaleDateString('en-IN'), title: new Date(caseData.updated_at).toLocaleString('en-IN') },
                        ]}
                    />
                </div>
            </div>

            {/* ── Body ──────────────────────────────────────────────── */}
            <div className="flex-1 overflow-auto p-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-7xl mx-auto">

                    {/* ── LEFT COLUMN ── */}
                    <div className="lg:col-span-2 space-y-4">

                        {/* Case Details — Inline Editable */}
                        <div className="bg-card border border-border rounded-lg p-4">
                            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Case Details
                                <span className="text-xs ml-1 opacity-50">( click any field to edit )</span>
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <InlineField label="Case Title" value={caseData.title} onSave={v => handleFieldSave('title', v)} placeholder="Enter case title" />
                                <InlineField label="FIR Number" value={caseData.fir_number || ''} onSave={v => handleFieldSave('fir_number', v)} placeholder="e.g. FIR-2024-001" />
                                <InlineField label="Investigating Officer" value={caseData.officer_name || ''} onSave={v => handleFieldSave('officer_name', v)} placeholder="Officer Name" />
                                <InlineField
                                    label="Status" value={caseData.status}
                                    onSave={v => handleFieldSave('status', v)}
                                    type="select" options={['OPEN', 'PROCESSING', 'CLOSED', 'ARCHIVED']}
                                />
                                <div className="col-span-2">
                                    <InlineField label="Notes" value={caseData.notes || ''} onSave={v => handleFieldSave('notes', v)} placeholder="Add case notes…" multiline />
                                </div>
                            </div>
                        </div>

                        {/* Saved Investigations — Expandable Drawers */}
                        <div className="bg-card border border-border rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between p-4 border-b border-border flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                    <Activity className={`w-5 h-5 ${brandIconAccent}`} />
                                    <h3 className="font-medium">Case Investigations</h3>
                                    <span className="px-2 py-0.5 text-xs bg-secondary rounded-full">{investigations.length}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                        onClick={handleRunAutoInvestigate}
                                        disabled={runBusy || !canRunCase}
                                        aria-busy={runBusy}
                                        data-testid="case-auto-investigate"
                                        data-tour="case-auto-investigate"
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-gradient-to-r from-amber-500/20 to-primary/20 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={!canRunCase
                                            ? VIEWER_ACTION_REASON
                                            : runBusy
                                            ? 'An Auto-Investigate run is in progress for this case'
                                            : 'Run the case worker over the seeds and notes of this case; the run is recorded under Runs'}
                                    >
                                        {runBusy ? <Loader2 className="w-4 h-4 animate-spin text-amber-500" /> : <Sparkles className="w-4 h-4 text-amber-500" />}
                                        {jobRunning
                                            ? `Investigating… ${formatDurationSeconds(jobElapsed)}`
                                            : autoInvestigating
                                                ? `Investigating… ${Math.floor(autoInvestigateElapsed / 60)}m ${String(autoInvestigateElapsed % 60).padStart(2, '0')}s`
                                                : startingRun ? 'Starting…' : 'Auto-Investigate'}
                                    </button>
                                    <button
                                        onClick={() => navigate(`/cases/${id}/canvas`)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-secondary/80 hover:bg-secondary text-foreground transition-colors font-medium border border-border"
                                        title="Open Multi-Modal Fusion Canvas (Telecom, Banking, Corporate & Crypto)"
                                    >
                                        <Layers className="w-4 h-4 text-amber-400" /> Fusion Canvas
                                    </button>
                                    <button
                                        onClick={() => {
                                            setWorkbenchEntryPreferFresh(true);
                                            setWorkbenchEntryOpen(true);
                                        }}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md ${brandGhostBtn}`}
                                    >
                                        <Plus className="w-4 h-4" /> New Crypto Trace
                                    </button>
                                </div>
                            </div>
                            {jobRunning && (
                                <div
                                    className="px-4 py-2 border-b border-border bg-primary/5 text-xs text-primary flex items-center gap-2"
                                    data-testid="case-auto-investigate-progress"
                                    aria-live="polite"
                                >
                                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden />
                                    <span className="truncate">
                                        Auto-Investigate running · {formatDurationSeconds(jobElapsed)}
                                        {' · '}{jobProgressText || 'waiting for the first step'}
                                        {jobWatch.error ? ' · status check failed, retrying' : ''}
                                    </span>
                                </div>
                            )}
                            {investigations.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground">
                                    <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                    <p>No saved investigations yet.</p>
                                    <p className="text-sm mt-1">Run Auto-Investigate to automatically map out indicators, or launch Fusion Canvas / Crypto Trace.</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-border">
                                    {investigations.map(inv => (
                                        <div key={inv.id} className="group">
                                            <InvestigationCard
                                                inv={inv}
                                                caseId={id!}
                                                onUpdated={updated => setInvestigations(prev => prev.map(i => i.id === updated.id ? updated : i))}
                                                onDeleted={invId => setInvestigations(prev => prev.filter(i => i.id !== invId))}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Runs — Auto-Investigate runs of this case, newest first */}
                        <CaseRunsSection
                            caseId={id!}
                            runs={runs}
                            loading={runsLoading}
                            error={runsError}
                            onRefresh={refreshRuns}
                            liveJob={jobWatch.job}
                            now={clock}
                            onCancel={cancelRun}
                        />

                        {/* Next steps — suggestions of the latest recorded run (OD10) */}
                        <NextStepsPanel
                            caseId={id!}
                            runs={runs}
                            refreshKey={runsNonce}
                            runBusy={runBusy}
                            onJobStarted={(job) => {
                                if (!job?.job_id) return;
                                setActiveJobLocalStart(Date.now());
                                setActiveJobId(job.job_id);
                                refreshRuns();
                            }}
                        />

                        {/* Ask the record — one bounded question of the latest finished run (PIVOT01; officer tier, a lead) */}
                        {(() => {
                            const recorded = latestRecordedRun(runs);
                            return recorded && !runBusy ? (
                                <PivotPanel caseId={id!} runRef={String(recorded.event_id)} />
                            ) : null;
                        })()}

                        {/* Reports — stored report artifacts across the case's investigations */}
                        <CaseReportsSection
                            caseId={id!}
                            investigations={investigations.map((inv) => ({ id: inv.id, name: inv.name }))}
                        />

                        {/* Data sources — propose a source met in this case for review (W3-02) */}
                        <ProposeSourceCard caseId={id!} />

                        {/* Case Addresses */}
                        <div className="bg-card border border-border rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between p-4 border-b border-border">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-primary" />
                                    <h3 className="font-medium">Case Addresses</h3>
                                    <span className="px-2 py-0.5 text-xs bg-secondary rounded-full">{watchlistItems.length}</span>
                                </div>
                                <button
                                    onClick={() => {
                                        setWorkbenchEntryPreferFresh(true);
                                        setWorkbenchEntryOpen(true);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-brand-gold-hover rounded-md font-medium transition-colors"
                                >
                                    <Plus className="w-4 h-4" /> Add Address
                                </button>
                            </div>
                            {watchlistItems.length === 0 ? (
                                <div className="p-6 text-center text-muted-foreground text-sm">No addresses linked. Open in Workbench to add addresses.</div>
                            ) : (
                                <div className="divide-y divide-border max-h-60 overflow-y-auto">
                                    {watchlistItems.map(item => (
                                        <div key={item.id} className="p-3 hover:bg-secondary/30 flex items-center justify-between cursor-pointer group" onClick={() => navigate(`/workbench?chain=${item.chain}&address=${item.address}&case_id=${id}`)}>
                                            <div>
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className="px-1.5 py-0.5 text-xs bg-secondary rounded font-mono">{item.chain}</span>
                                                    <span className="font-medium text-sm">{item.label || 'Unlabeled'}</span>
                                                </div>
                                                <p className="text-xs text-muted-foreground font-mono">{item.address.slice(0, 18)}…{item.address.slice(-8)}</p>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Evidence Locker */}
                        <div className="bg-card border border-border rounded-lg overflow-hidden">
                            <div className="flex items-start justify-between p-4 border-b border-border">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <ShieldCheck className="w-5 h-5 text-emerald-400" />
                                        <h3 className="font-medium">Evidence Locker</h3>
                                        <span className="px-2 py-0.5 text-xs bg-secondary rounded-full">{evidence.length}</span>
                                        <span
                                            className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-secondary text-muted-foreground rounded-full"
                                            title="Investigator-authored context. Integrity is self-asserted — the SHA-256 field is a string you paste in, not a hash of an uploaded file. Roughly Tier 3–4 (context / OSINT) in the evidence model; generated legal notices are procedural artifacts."
                                        >
                                            Context · Tier 3–4
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1 max-w-[420px]">
                                        Your working notebook — notes, tx/wallet references, screenshots, and legal notices. Integrity is self-asserted (no file upload or server-side hashing).
                                    </p>
                                </div>
                                <button onClick={() => setShowEvidenceModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 rounded-md flex-shrink-0">
                                    <Plus className="w-4 h-4" /> Add Evidence
                                </button>
                            </div>
                            {evidence.length === 0 ? (
                                <div className="p-6 text-center text-muted-foreground text-sm">No evidence recorded yet. Add notes, screenshots, tx refs, and hashes here.</div>
                            ) : (
                                <div className="divide-y divide-border max-h-96 overflow-y-auto">
                                    {evidence.map(ev => ev.evidence_type === 'notice' ? (
                                        <NoticeEvidenceRow
                                            key={ev.id}
                                            ev={ev}
                                            onDownload={() => handleDownloadNotice(ev)}
                                            onMarkSent={() => handleMarkNoticeSent(ev)}
                                            onMarkResponded={() => handleMarkNoticeResponded(ev)}
                                            onDelete={() => handleDeleteEvidence(ev.id)}
                                        />
                                    ) : ev.evidence_type === 'bank_statement' ? (
                                        <BankStatementEvidenceRow
                                            key={ev.id}
                                            ev={ev}
                                            onDelete={() => handleDeleteEvidence(ev.id)}
                                            onOpenCanvas={() => navigate(`/cases/${id}/canvas`)}
                                        />
                                    ) : (
                                        <div key={ev.id} className="p-3 hover:bg-secondary/30">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="px-1.5 py-0.5 text-xs bg-secondary rounded uppercase">{ev.evidence_type}</span>
                                                        <p className="text-sm font-medium truncate">{ev.title}</p>
                                                    </div>
                                                    {ev.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ev.description}</p>}
                                                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-2">
                                                        <span>Source: {ev.source || 'manual'}</span>
                                                        <span>{new Date(ev.created_at).toLocaleString()}</span>
                                                        {ev.hash_sha256 && <span className="font-mono">SHA: {ev.hash_sha256.slice(0, 12)}…</span>}
                                                    </div>
                                                </div>
                                                <button onClick={() => handleDeleteEvidence(ev.id)} className="p-1.5 hover:bg-red-500/10 text-red-400 rounded flex-shrink-0">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Forensic Records — Multi-sheet Excel/CSV responses */}
                        <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-end mb-3">
                                <button
                                    onClick={() => setShowDeviceUpload(true)}
                                    className="px-3 py-1.5 text-xs bg-primary/90 hover:bg-primary text-primary-foreground rounded-md"
                                    title="Upload a Cellebrite UFDR / EnCase E01 device extraction, or re-import an offline .manifest.ndjson"
                                >
                                    Upload Device Extraction / Manifest
                                </button>
                            </div>
                            <ForensicEvidenceSection caseId={id!} />
                        </div>
                        </div>

                    {/* ── RIGHT SIDEBAR ── */}
                    <div className="space-y-4">
                        {/* Export Actions */}
                        <div className="bg-card border border-border rounded-lg p-4">
                            <h3 className="font-medium mb-3 text-sm">Export Case</h3>
                            <div className="space-y-2">
                                <button onClick={() => navigate(`/cases/${id}/print`)} data-testid="case-full-print-btn"
                                    title="The case-worker run's findings, notices and matrix, then the evidence locker, as one document to print"
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md ${brandGhostBtn}`}>
                                    <Download className="w-4 h-4" />
                                    Print full case (with investigation)
                                </button>
                                <button onClick={() => handleExportCase('pdf')} disabled={exporting !== null}
                                    title="The case row, saved investigations and evidence only; it holds no case-worker findings"
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md disabled:opacity-60">
                                    <Download className="w-4 h-4" />
                                    {exporting === 'pdf' ? 'Exporting…' : 'Case record PDF (no investigation)'}
                                </button>
                                <div className="grid grid-cols-2 gap-2">
                                    <button onClick={() => handleExportCase('json')} disabled={exporting !== null} className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md disabled:opacity-60">
                                        {exporting === 'json' ? <Loader2 className="w-3 h-3 animate-spin" /> : null} JSON
                                    </button>
                                    <button onClick={() => handleExportCase('csv')} disabled={exporting !== null} className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md disabled:opacity-60">
                                        {exporting === 'csv' ? <Loader2 className="w-3 h-3 animate-spin" /> : null} CSV
                                    </button>
                                </div>
                            </div>
                        </div>

                        <NoticeTemplatesPanel caseId={id!} />

                        {/* Collaborators */}
                        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="font-medium text-sm flex items-center gap-1.5">
                                    <Users className="w-4 h-4" /> Collaborators
                                    <span className="text-xs text-muted-foreground font-normal">({collaborators.length})</span>
                                </h3>
                                {canManageCollabs && (
                                    <button
                                        onClick={openAddCollab}
                                        className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary hover:bg-primary/20 rounded"
                                        title="Share this case with another user"
                                    >
                                        <UserPlus className="w-3 h-3" /> Add
                                    </button>
                                )}
                            </div>
                            {collaborators.length === 0 ? (
                                <p className="text-xs text-muted-foreground">
                                    No collaborators. {canManageCollabs ? 'Click Add to share this case.' : ''}
                                </p>
                            ) : (
                                <div className="space-y-1.5">
                                    {collaborators.map((c) => (
                                        <div key={c.user_id} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-secondary/30 rounded">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm truncate">{c.full_name || c.username || `User #${c.user_id}`}</p>
                                                {c.username && c.full_name && (
                                                    <p className="text-xs text-muted-foreground font-mono truncate">{c.username}</p>
                                                )}
                                            </div>
                                            {canManageCollabs && (
                                                <button
                                                    onClick={() => handleRemoveCollab(c.user_id, c.full_name || c.username || `User #${c.user_id}`)}
                                                    className="p-1 text-red-400 hover:bg-red-500/10 rounded flex-shrink-0"
                                                    title="Remove"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Metadata */}
                        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
                            <h3 className="font-medium text-sm">Case Owner & Timestamps</h3>
                            {(caseData.creator_full_name || caseData.creator_username || caseData.creator_email) && (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-0.5">Created by</p>
                                    <p className="text-sm font-medium">{caseData.creator_full_name || caseData.creator_username || 'Unknown'}</p>
                                    {caseData.creator_email && (
                                        <p className="text-xs text-muted-foreground font-mono break-all">{caseData.creator_email}</p>
                                    )}
                                </div>
                            )}
                            <div>
                                <p className="text-xs text-muted-foreground mb-0.5">Created</p>
                                <p className="text-sm">{new Date(caseData.created_at).toLocaleString('en-IN')}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-0.5">Last Updated</p>
                                <p className="text-sm">{new Date(caseData.updated_at).toLocaleString('en-IN')}</p>
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="bg-card border border-border rounded-lg p-4">
                            <h3 className="font-medium text-sm mb-2">Investigation Badges</h3>
                            <div className="space-y-1.5 text-xs text-muted-foreground">
                                <div className="flex items-center gap-2"><Brain className="w-3.5 h-3.5 text-primary" /> AI Intelligence Note saved</div>
                                <div className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-amber-400" /> Pattern scan results saved</div>
                                <div className="flex items-center gap-2"><Image className="w-3.5 h-3.5 text-emerald-400" /> Graph screenshot captured</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Evidence Modal ─────────────────────────────────────── */}
            {showEvidenceModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card border border-border rounded-xl w-full max-w-lg p-6 shadow-2xl">
                        <h2 className="text-xl font-bold mb-4">Add Evidence</h2>
                        <form onSubmit={handleCreateEvidence} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Type</label>
                                    <select className="w-full bg-background border border-border rounded p-2" value={evidenceForm.evidence_type} onChange={e => setEvidenceForm({ ...evidenceForm, evidence_type: e.target.value })}>
                                        {['note', 'tx', 'wallet', 'document', 'screenshot', 'chat'].map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-1">Source</label>
                                    <input type="text" className="w-full bg-background border border-border rounded p-2" placeholder="manual / export / exchange" value={evidenceForm.source} onChange={e => setEvidenceForm({ ...evidenceForm, source: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Title</label>
                                <input type="text" required className="w-full bg-background border border-border rounded p-2" placeholder="Evidence title" value={evidenceForm.title} onChange={e => setEvidenceForm({ ...evidenceForm, title: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
                                <textarea className="w-full bg-background border border-border rounded p-2 h-20" placeholder="Context / why this matters" value={evidenceForm.description} onChange={e => setEvidenceForm({ ...evidenceForm, description: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-muted-foreground mb-1">SHA-256 (optional)</label>
                                <input type="text" className="w-full bg-background border border-border rounded p-2 font-mono text-xs" placeholder="Paste SHA-256 hash" value={evidenceForm.hash_sha256} onChange={e => setEvidenceForm({ ...evidenceForm, hash_sha256: e.target.value })} />
                            </div>
                            <div className="flex justify-end gap-3">
                                <button type="button" onClick={() => setShowEvidenceModal(false)} className="px-4 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md">Cancel</button>
                                <button type="submit" className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 rounded-md">Save Evidence</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Collaborator Modal */}
            {showAddCollab && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddCollab(false)}>
                    <div className="bg-secondary/30 border border-border rounded-xl w-full max-w-md max-h-[70vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 border-b border-border flex items-center justify-between">
                            <h2 className="text-lg font-semibold flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add Collaborator</h2>
                            <button onClick={() => setShowAddCollab(false)} className="p-1 hover:bg-secondary rounded"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="px-4 pt-3 text-xs text-muted-foreground">
                            Select a user to share this case with. They will gain view access.
                        </p>
                        <div className="overflow-y-auto flex-1 p-2">
                            {allUsers.length === 0 ? (
                                <div className="flex items-center justify-center p-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
                            ) : (
                                allUsers
                                    .filter(u => u.is_active !== false)
                                    .filter(u => u.id !== caseData?.created_by)
                                    .filter(u => !collaborators.some(c => c.user_id === u.id))
                                    .map(u => (
                                        <button
                                            key={u.id}
                                            disabled={addingCollab}
                                            onClick={() => handleAddCollab(u.id)}
                                            className="w-full text-left flex items-center justify-between px-3 py-2 hover:bg-secondary/40 rounded disabled:opacity-60"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm truncate">{u.full_name || u.username}</p>
                                                <p className="text-xs text-muted-foreground font-mono truncate">{u.email || u.username} · {u.role}</p>
                                            </div>
                                            <Plus className="w-4 h-4 text-primary flex-shrink-0" />
                                        </button>
                                    ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {confirmDialog}

            {id && (
                <CaseWorkbenchEntryModal
                    isOpen={workbenchEntryOpen}
                    caseId={id}
                    caseTitle={caseData?.title}
                    preferFresh={workbenchEntryPreferFresh}
                    onClose={() => setWorkbenchEntryOpen(false)}
                />
            )}

            <ForensicImportModal
                isOpen={showDeviceUpload}
                onClose={() => setShowDeviceUpload(false)}
                preselectedCaseId={id}
            />
        </div>
    );
};
